import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addendumService,
  type AddendumDraftAccess,
  type AddendumDraftInput,
} from "../services/addendum.service";
import type { ReportPreviewEvidence } from "../services/signOff.service";

function invalidatePathologistReviewQueries(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.invalidateQueries({ queryKey: ["pathologist-review-queue"] });
  queryClient.invalidateQueries({ queryKey: ["pathologist-review-stats"] });
}

export const addendumDraftQueryKey = (caseId: string) => [
  "case",
  caseId,
  "addendum-draft",
];

export function useAddendumEligibility(caseId: string, enabled = true) {
  return useQuery({
    queryKey: ["case", caseId, "addendum-eligibility"],
    queryFn: () => addendumService.getEligibility(caseId),
    enabled: enabled && Boolean(caseId),
  });
}

export function useFrontOfficeTissueCypherAddendumEligibility(
  caseId: string,
  enabled = true
) {
  return useQuery({
    queryKey: [
      "case",
      caseId,
      "front-office-tissue-cypher-addendum-eligibility",
    ],
    queryFn: () =>
      addendumService.getFrontOfficeTissueCypherEligibility(caseId),
    enabled: enabled && Boolean(caseId),
  });
}

export function useAddendumDraft(
  caseId: string,
  enabled = true,
  access: AddendumDraftAccess = "ordinary"
) {
  return useQuery({
    queryKey: addendumDraftQueryKey(caseId),
    queryFn: () => addendumService.getDraft(caseId, access),
    enabled: enabled && Boolean(caseId),
  });
}

export function useAddendumOptions(
  caseId: string,
  enabled = true,
  access: AddendumDraftAccess = "ordinary"
) {
  return useQuery({
    queryKey: ["case", caseId, "addendum-options"],
    queryFn: () => addendumService.getOptions(caseId, access),
    enabled: enabled && Boolean(caseId),
  });
}

export function useSaveAddendumDraft(
  caseId: string,
  access: AddendumDraftAccess = "ordinary"
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      draftId,
      input,
    }: {
      draftId?: string;
      input: AddendumDraftInput;
    }) =>
      draftId
        ? addendumService.updateDraft(caseId, draftId, input, access)
        : addendumService.createDraft(caseId, input, access),
    onSuccess: (draft) => {
      queryClient.setQueryData(addendumDraftQueryKey(caseId), draft);
      invalidatePathologistReviewQueries(queryClient);
    },
  });
}

export function useDeleteAddendumDraft(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) =>
      addendumService.deleteDraft(caseId, draftId),
    onSuccess: () => {
      queryClient.setQueryData(addendumDraftQueryKey(caseId), null);
      invalidatePathologistReviewQueries(queryClient);
    },
  });
}

export function useSignOutAddendum(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      draftId,
      revision,
      sessionId,
      password,
      previewEvidence,
      reviewedPanelKeys,
    }: {
      draftId: string;
      revision: number;
      sessionId?: string | null;
      password?: string;
      previewEvidence?: ReportPreviewEvidence;
      reviewedPanelKeys?: string[];
    }) =>
      addendumService.signOutDraft(caseId, draftId, {
        revision,
        sessionId,
        password,
        previewEvidence,
        reviewedPanelKeys,
      }),
    onSuccess: () => {
      queryClient.setQueryData(addendumDraftQueryKey(caseId), null);
      queryClient.invalidateQueries({
        queryKey: ["case", caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["document-view", caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["sign-off-history", caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["report-delivery-recall", caseId],
      });
      invalidatePathologistReviewQueries(queryClient);
    },
  });
}
